// One tune list, two text formats: an iReal Pro link or an ABC tunebook.
//
// Both index cheaply — titles and composers, no music read — and keep each
// tune as a self-contained CHUNK that parses when it is opened: an iReal song
// chunk, or one ABC tune with its file header's defaults written in. The
// SCHEME says how to read a chunk ('irealb', 'irealbook' or 'abc'), and
// travels with it, so a playlist row, a recent and the loaded list all carry
// exactly the tune they name in either format.

import { isAbcSource, peekAbcMeta, splitAbcBook } from './abc/book';
import { parseIRealIndex, peekSongMeta, type IRealIndex } from './ireal/parse';

/** The scheme an ABC chunk travels under. */
export const ABC_SCHEME = 'abc';

export function isAbcScheme(scheme: string): boolean {
  return scheme === ABC_SCHEME;
}

/** Index an iReal Pro link or an ABC file; throws on text that is neither. */
export function parseTuneIndex(source: string): IRealIndex {
  if (!isAbcSource(source)) {
    if (!/ireal/i.test(source)) throw new Error('Not an iReal Pro link or an ABC tune.');
    return parseIRealIndex(source);
  }
  const book = splitAbcBook(source);
  return {
    scheme: ABC_SCHEME,
    ...(book.name ? { name: book.name } : {}),
    songs: book.tunes.map((tune) => ({
      title: tune.title,
      composer: tune.composer,
      chunk: tune.chunk,
    })),
  };
}

export interface TuneChunkMeta {
  style?: string;
  key?: string;
  /** A written melody (an iReal chunk never has one). */
  melody: boolean;
  /** Sung words (an iReal chunk never has any). */
  lyrics: boolean;
}

/** Cheap facts about one chunk for the browser's badges and filters. */
export function peekTuneMeta(scheme: string, chunk: string): TuneChunkMeta {
  if (isAbcScheme(scheme)) return peekAbcMeta(chunk);
  return { ...peekSongMeta(scheme, chunk), melody: false, lyrics: false };
}
